"use client";
import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

interface FAQItem {
	question: string;
	answer: string;
	category: string;
}

const faqData: FAQItem[] = [
	{
		category: "Getting Started",
		question: "What is MyGeoTokens?",
		answer:
			"MyGeoTokens is a decentralized application that allows you to drop, collect, and trade location-based NFTs on the Solana blockchain. You can create geospatial NFT collections and discover NFTs in real-world locations.",
	},
	{
		category: "Getting Started",
		question: "What wallet do I need to use MyGeoTokens?",
		answer:
			"You need a Solana-compatible wallet. MGT Wallet is built in; you can also use Solflare or other Web3 wallets that support Solana. Make sure your wallet has SOL for transaction fees.",
	},
	{
		category: "Getting Started",
		question: "How do I connect my wallet?",
		answer:
			"Click the wallet connection indicator in the header, select your preferred wallet, and follow the prompts to connect. Make sure your wallet is installed and unlocked in your browser.",
	},
	{
		category: "NFT Operations",
		question: "How do I drop an NFT?",
		answer:
			"Navigate to the Drop Stash page, upload your NFT metadata and images, set your price and location parameters, then confirm the transaction in your wallet. Your NFT will be deployed on-chain and appear on the map.",
	},
	{
		category: "NFT Operations",
		question: "What file formats are supported for NFTs?",
		answer:
			"We support common image formats including JPG, PNG, and GIF. Files should be under 20MB in size for optimal performance.",
	},
	{
		category: "NFT Operations",
		question: "How do I collect NFTs from locations?",
		answer:
			"Navigate to locations marked on the map, click on the NFT pins, and purchase available NFTs. You must be within the designated geofence area to collect location-based NFTs.",
	},
	{
		category: "Blockchain & Fees",
		question: "What are network fees?",
		answer:
			"Network fees (gas fees) are required for all Solana blockchain transactions. These fees go to network validators and are typically very low on Solana (usually less than $0.01).",
	},
	{
		category: "Blockchain & Fees",
		question: "Why do I need SOL in my wallet?",
		answer:
			"SOL is required to pay for transaction fees on the Solana blockchain. You need SOL for dropping NFTs, purchasing NFTs, and any other blockchain interactions.",
	},
	{
		category: "Blockchain & Fees",
		question: "Are transactions reversible?",
		answer:
			"No, blockchain transactions are permanent and irreversible. Always verify wallet addresses and transaction details before confirming.",
	},
	{
		category: "Account & Security",
		question: "How do I set up my profile?",
		answer:
			"After connecting your wallet, navigate to the Profile page to complete your profile setup with personal information, location details, and profile picture.",
	},
	{
		category: "Account & Security",
		question: "Is my personal information secure?",
		answer:
			"We use industry-standard encryption and security measures to protect your personal data. However, blockchain transactions and wallet addresses are publicly visible on the Solana network.",
	},
	{
		category: "Account & Security",
		question: "Can I delete my account?",
		answer:
			"You can delete your account and personal data from our platform, but blockchain transactions and NFTs cannot be removed due to the immutable nature of the blockchain.",
	},
	{
		category: "Technical Issues",
		question: "Why won't my wallet connect?",
		answer:
			"Ensure your wallet extension is installed, unlocked, and updated. Try refreshing the page or switching to a different supported wallet. Check that you're on the correct network (Solana mainnet).",
	},
	{
		category: "Technical Issues",
		question: "My transaction failed, what do I do?",
		answer:
			"Failed transactions are usually due to insufficient SOL for fees, network congestion, or slippage. Check your SOL balance and try again. If issues persist, contact support.",
	},
	{
		category: "Technical Issues",
		question: "Why can't I see my NFTs?",
		answer:
			"It may take a few minutes for NFTs to appear after minting or purchasing. Refresh the page and check your wallet. Ensure you're connected to the correct wallet address.",
	},
];

export default function FAQs() {
	const [openItems, setOpenItems] = useState<string[]>([]);
	const [selectedCategory, setSelectedCategory] = useState<string>("All");

	const categories = [
		"All",
		...Array.from(new Set(faqData.map((item) => item.category))),
	];

	const filteredFAQs =
		selectedCategory === "All"
			? faqData
			: faqData.filter((item) => item.category === selectedCategory);

	const toggleItem = (question: string) => {
		setOpenItems((prev) =>
			prev.includes(question)
				? prev.filter((i) => i !== question)
				: [...prev, question]
		);
	};

	return (
		<div className="min-h-full bg-mgt-black px-4 py-8 md:py-12">
			<div className="max-w-4xl mx-auto">
				<div className="text-center mb-12">
					<h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
						Frequently Asked Questions
					</h1>
					<p className="text-off-white text-lg">
						Find answers to common questions about MyGeoTokens
					</p>
				</div>

				{/* Category Filter */}
				<div className="mb-8">
					<div className="flex flex-wrap gap-2 justify-center">
						{categories.map((category) => (
							<button
								key={category}
								onClick={() => setSelectedCategory(category)}
								className={`px-4 py-2 rounded-full transition-colors duration-200 ${
									selectedCategory === category
										? "bg-mgt-green text-mgt-black font-medium"
										: "bg-brown text-off-white hover:bg-brown/80"
								}`}
							>
								{category}
							</button>
						))}
					</div>
				</div>

				{/* FAQ Items */}
				<div className="space-y-4">
					{filteredFAQs.map((item) => (
						<div
							key={item.question}
							className="bg-brown rounded-xl overflow-hidden border border-brown/50"
						>
							<button
								onClick={() => toggleItem(item.question)}
								aria-expanded={openItems.includes(item.question)}
								className="w-full p-6 text-left flex items-center justify-between hover:bg-brown/80 transition-colors duration-200"
							>
								<div>
									<span className="text-mgt-green text-sm font-medium block mb-1">
										{item.category}
									</span>
									<h3 className="text-white font-semibold text-lg">
										{item.question}
									</h3>
								</div>
								<div className="ml-4 flex-shrink-0">
									{openItems.includes(item.question) ? (
										<ChevronUp className="w-5 h-5 text-mgt-green" />
									) : (
										<ChevronDown className="w-5 h-5 text-mgt-green" />
									)}
								</div>
							</button>

							{openItems.includes(item.question) && (
								<div className="px-6 pb-6">
									<div className="border-t border-off-white/20 pt-4">
										<p className="text-off-white leading-relaxed">
											{item.answer}
										</p>
									</div>
								</div>
							)}
						</div>
					))}
				</div>

				{/* Contact Section */}
				<div className="mt-12 bg-brown rounded-2xl p-6 md:p-8 text-center">
					<h2 className="text-2xl font-bold text-mgt-green mb-4">
						Still Have Questions?
					</h2>
					<p className="text-off-white mb-6">
						Can&apos;t find the answer you&apos;re looking for? Join our Discord
						community for support and assistance.
					</p>
					<div className="space-y-4">
						<a
							href="https://discord.gg/UpxTdUVkKy"
							target="_blank"
							rel="noopener noreferrer"
							className="inline-block bg-mgt-green text-mgt-black px-6 py-3 rounded-full font-medium hover:bg-mgt-yellow transition-colors duration-200"
						>
							Join Discord Support
						</a>
						<div className="text-sm text-off-white">
							<p>Discord: discord.gg/UpxTdUVkKy</p>
							<p>Get real-time help from our community and support team</p>
						</div>
					</div>
				</div>

				<div className="border-t border-off-white/20 pt-6 mt-8">
					<p className="text-sm text-off-white text-center">
						Last updated: May 11, 2026
					</p>
					<p className="text-sm text-off-white text-center mt-2">
						© {new Date().getFullYear()} MyGeoTokens Inc. All rights reserved.
					</p>
				</div>
			</div>
		</div>
	);
}
